
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
export const metadata = {
  toc: true,
  maxTocLevel: 5,
  layout: 'guide',
  description: 'Connect an LLM application to Plaid Dashboard tools'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Dashboard MCP server' subtitle={metadata.description} mdxType="Header" />
    <p>{`Plaid offers two `}<a parentName="p" {...{
        "href": "https://modelcontextprotocol.io/"
      }}>{`Model Context Protocol`}</a>{` (MCP) servers:`}</p>
    <ul>
      <li parentName="ul">{`The `}<strong parentName="li">{`Dashboard MCP server`}</strong>{` is a remote MCP server hosted by Plaid. It provides Production diagnostics and analytics tools, including tools for debugging Items, reviewing Link conversion data, and checking usage metrics.`}</li>
      <li parentName="ul">{`The `}<strong parentName="li"><a parentName="strong" {...{
            "href": "https://github.com/plaid/ai-coding-toolkit/tree/main/sandbox"
          }}>{`Plaid AI coding toolkit`}</a></strong>{` includes a local MCP server for development. It runs as a subprocess of an MCP client, such as Claude, Cursor, Codex, VS Code, or Zed, and provides tools for mock data generation, documentation search, Sandbox tokens, and webhook simulation.`}</li>
    </ul>
    <p>{`This article covers the Dashboard MCP server. You can connect to it from an MCP client, or you can use it in a model provider's API as a remote MCP server.`}</p>
    <Callout warning mdxType="Callout">
      <p>{`The Dashboard MCP server is under active development. Breaking changes may occur, and Plaid currently provides limited support.`}</p>
    </Callout>
    <h4 {...{
      "id": "connection-options"
    }}>{`Connection options`}</h4>
    <h5 {...{
      "id": "requirements"
    }}>{`Requirements`}</h5>
    <p>{`The Dashboard MCP server works with Production data only. Your team must be approved for Production access with at least one Plaid product to use the MCP server.`}</p>
    <h5 {...{
      "id": "mcp-client-setup"
    }}>{`MCP client setup`}</h5>
    <p>{`Use this option for an MCP client or hosted assistant, such as Claude.ai, ChatGPT, or another client that supports remote MCP server URLs.`}</p>
    <p>{`In your client's MCP settings, add the Dashboard MCP server URL:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-text",
        "metastring": "switcher=false #Dashboard MCP server URL",
        "switcher": "false",
        "#Dashboard": true,
        "MCP": true,
        "server": true,
        "URL": true
      }}>{`https://api.dashboard.plaid.com/mcp/
`}</code></pre>
    <p>{`If your client asks for the transport or protocol, choose Streamable HTTP.`}</p>
    <p>{`For Claude.ai setup, see `}<a parentName="p" {...{
        "href": "https://support.claude.com/en/articles/11175166-get-started-with-custom-connectors-using-remote-mcp"
      }}>{`Claude's custom connector documentation`}</a>{`.`}</p>
    <h5 {...{
      "id": "api-integration-with-remote-mcp"
    }}>{`API integration with remote MCP`}</h5>
    <p>{`Use this option when your application calls a model provider API and lets the model call Dashboard MCP tools during that request. This is an API integration with a remote MCP server. OpenAI exposes remote MCP servers as `}<inlineCode parentName="p">{`mcp`}</inlineCode>{` tools in the Responses API. Anthropic calls this feature the MCP connector in the Messages API.`}</p>
    <p>{`For this flow, your backend creates or refreshes a Plaid OAuth token and passes it to the model provider SDK with the Dashboard MCP server URL. The field name depends on the provider. OpenAI uses `}<inlineCode parentName="p">{`authorization`}</inlineCode>{`; Anthropic uses `}<inlineCode parentName="p">{`authorization_token`}</inlineCode>{`.`}</p>
    <p>{`Your model provider account must have access to remote MCP servers or hosted tools.`}</p>
    <h5 {...{
      "id": "creating-an-oauth-token"
    }}>{`Creating an OAuth token`}</h5>
    <p>{`To connect to the Dashboard MCP server, first create an OAuth token with the `}<inlineCode parentName="p">{`mcp:dashboard`}</inlineCode>{` scope and the `}<inlineCode parentName="p">{`client_credentials`}</inlineCode>{` grant type by calling `}<inlineCode parentName="p">{`/oauth/token`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Creating an access token",
        "switcher": "false",
        "#Creating": true,
        "an": true,
        "access": true,
        "token": true
      }}>{`curl -X POST https://production.plaid.com/oauth/token \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "client_secret": "YOUR_PRODUCTION_SECRET",
    "grant_type": "client_credentials",
    "scope": "mcp:dashboard"
  }'
`}</code></pre>
    <p>{`The response includes an `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` for Dashboard MCP requests and a `}<inlineCode parentName="p">{`refresh_token`}</inlineCode>{` for requesting a new access token.`}</p>
    <h5 {...{
      "id": "passing-the-mcp-server-to-a-model-api"
    }}>{`Passing the MCP server to a model API`}</h5>
    <p>{`The Dashboard MCP server is available at `}<inlineCode parentName="p">{`https://api.dashboard.plaid.com/mcp/`}</inlineCode>{` and uses the Streamable HTTP protocol. When you communicate with the MCP server directly, pass the `}<inlineCode parentName="p">{`access_token`}</inlineCode>{` from the authorization step in an `}<inlineCode parentName="p">{`Authorization: Bearer <access_token>`}</inlineCode>{` header.`}</p>
    <p>{`The connection method depends on the LLM client library. Most provider SDKs expose remote MCP servers as tools that the model can call. See your model provider's documentation, such as `}<a parentName="p" {...{
        "href": "https://developers.openai.com/api/docs/guides/tools-connectors-mcp"
      }}>{`OpenAI`}</a>{` or `}<a parentName="p" {...{
        "href": "https://platform.claude.com/docs/en/agents-and-tools/mcp-connector"
      }}>{`Anthropic`}</a>{`. Use a current version of your model provider's client library.`}</p>
    <p>{`The following examples show how to access Link analytics through the Dashboard MCP server using the OpenAI or Anthropic Python client libraries.`}</p>
    <p>{`These examples use the following environment variables: `}<inlineCode parentName="p">{`PLAID_CLIENT_ID`}</inlineCode>{`, `}<inlineCode parentName="p">{`PLAID_PRODUCTION_SECRET`}</inlineCode>{`, and either `}<inlineCode parentName="p">{`OPENAI_API_KEY`}</inlineCode>{` or `}<inlineCode parentName="p">{`ANTHROPIC_API_KEY`}</inlineCode>{`.`}</p>
    <ContentSwitcher.Container id='example' names={['OpenAI', 'Anthropic']}>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-python",
            "metastring": "switcher=false #OpenAI Python example",
            "switcher": "false",
            "#OpenAI": true,
            "Python": true,
            "example": true
          }}>{`import json
import os

import plaid
from openai import OpenAI
from plaid.api import plaid_api
from plaid.api_client import ApiClient
from plaid.model.o_auth_grant_type import OAuthGrantType
from plaid.model.o_auth_token_request import OAuthTokenRequest

DASHBOARD_MCP_SERVER_URL = "https://api.dashboard.plaid.com/mcp/"


def get_plaid_client():
    configuration = plaid.Configuration(
        host=plaid.Environment.Production,
        api_key={
            "clientId": os.environ["PLAID_CLIENT_ID"],
            "secret": os.environ["PLAID_PRODUCTION_SECRET"],
        },
    )
    api_client = ApiClient(configuration)
    return plaid_api.PlaidApi(api_client)


def request_or_refresh_access_token():
    # This example creates a new access token each run.
    plaid_client = get_plaid_client()
    oauth_token_request = OAuthTokenRequest(
        grant_type=OAuthGrantType("client_credentials"),
        scope="mcp:dashboard",
    )

    response = plaid_client.oauth_token(oauth_token_request)
    return response.access_token


def main() -> None:
    """Retrieve Link analytics through the Dashboard MCP server."""

    client = OpenAI(api_key=os.environ["OPENAI_API_KEY"])
    dashboard_token = request_or_refresh_access_token()

    response = client.responses.create(
        model="gpt-5.5",
        tools=[
            {
                "type": "mcp",
                "server_label": "plaid",
                "server_url": DASHBOARD_MCP_SERVER_URL,
                "require_approval": "never",
                "authorization": dashboard_token,
            }
        ],
        input=(
            "Use the Dashboard MCP server to get Link analytics from "
            "2025-05-01 through 2025-05-08. Return Link opens, "
            "institution selections, and handoffs."
        ),
    )

    for item in response.output:
        if item.type == "mcp_list_tools":
            print("\\n=== MCP Tools ===")
            for tool in item.tools:
                print(f" - {tool['name']}")

        elif item.type == "mcp_call":
            print("\\n=== MCP Call ===")
            print(f"Tool: {item.name}")
            try:
                # The arguments field is a JSON string. Parse it for readability.
                args = json.loads(item.arguments)
            except (TypeError, json.JSONDecodeError):
                args = item.arguments
            print("Arguments:")
            print(json.dumps(args, indent=2))

        elif item.type == "message":
            print("\\n=== Assistant Message ===")
            # The content field is a list of ResponseOutputText objects.
            texts = []
            for part in item.content:
                # Depending on SDK version, \`text\` may be an attribute or dict key.
                text = getattr(part, "text", None) or (
                    part.get("text") if isinstance(part, dict) else None
                )
                if text:
                    texts.append(text)
            print("\\n".join(texts))

        else:
            print(f"\\n=== Unhandled output type: {item.type} ===")
            print(item)


if __name__ == "__main__":
    main()
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-text",
            "metastring": "switcher=false #Sample output",
            "switcher": "false",
            "#Sample": true,
            "output": true
          }}>{`=== MCP Tools ===
 - plaid_debug_item
 - plaid_get_link_analytics
 - plaid_get_tools_introduction
 - plaid_get_usages
 - plaid_list_teams

=== MCP Call ===
Tool: plaid_list_teams
Arguments:
{}

=== MCP Call ===
Tool: plaid_get_link_analytics
Arguments:
{
  "from_date": "2025-05-01",
  "team_id": "<team_id>",
  "to_date": "2025-05-08"
}

=== Assistant Message ===
From 2025-05-01 through 2025-05-08:
- Link opens: 58
- Institution selections: 29
- Handoffs: 18
`}</code></pre>
      </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <pre><code parentName="pre" {...{
            "className": "language-python",
            "metastring": "switcher=false #Anthropic Python example",
            "switcher": "false",
            "#Anthropic": true,
            "Python": true,
            "example": true
          }}>{`
import json
import os

import anthropic
import plaid
from plaid.api import plaid_api
from plaid.api_client import ApiClient
from plaid.model.o_auth_grant_type import OAuthGrantType
from plaid.model.o_auth_token_request import OAuthTokenRequest

DASHBOARD_MCP_SERVER_URL = "https://api.dashboard.plaid.com/mcp/"


def get_plaid_client():
    configuration = plaid.Configuration(
        host=plaid.Environment.Production,
        api_key={
            "clientId": os.environ["PLAID_CLIENT_ID"],
            "secret": os.environ["PLAID_PRODUCTION_SECRET"],
        },
    )
    api_client = ApiClient(configuration)
    return plaid_api.PlaidApi(api_client)


def request_or_refresh_access_token():
    # This example creates a new access token each run.
    plaid_client = get_plaid_client()
    oauth_token_request = OAuthTokenRequest(
        grant_type=OAuthGrantType("client_credentials"),
        scope="mcp:dashboard",
    )

    response = plaid_client.oauth_token(oauth_token_request)
    return response.access_token


def main() -> None:
    """Retrieve Link analytics through the Dashboard MCP server."""

    client = anthropic.Anthropic(api_key=os.environ["ANTHROPIC_API_KEY"])
    dashboard_token = request_or_refresh_access_token()
    response = client.beta.messages.create(
        betas=["mcp-client-2025-11-20"],
        model="claude-opus-5",
        mcp_servers=[
            {
                "type": "url",
                "url": DASHBOARD_MCP_SERVER_URL,
                "name": "Plaid Dashboard Server",
                "authorization_token": dashboard_token,
            }
        ],
        tools=[
            {"type": "mcp_toolset", "mcp_server_name": "Plaid Dashboard Server"}
        ],
        messages=[
            {
                "role": "user",
                "content": (
                    "Use the Plaid Dashboard Server to get Link analytics from "
                    "2025-05-01 through 2025-05-08. Return Link opens, "
                    "institution selections, and handoffs."
                ),
            }
        ],
        max_tokens=1024,
    )

    print("\\n=== Assistant Message ===")
    if response.content:
        for content in response.content:
            if content.type == "text" and content.text:
                print(content.text)
            elif content.type == "mcp_tool_use":
                print(f"\\nTool Call: {content.name}")
                print("Arguments:")
                print(json.dumps(content.input, indent=2))
            elif content.type == "mcp_tool_result":
                print("\\nTool Result:")
                if not content.is_error:
                    for result in content.content:
                        if result.type == "text":
                            print(result.text)
                else:
                    print("Error occurred during tool execution")
            print("-" * 80)


if __name__ == "__main__":
    main()

`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-text",
            "metastring": "switcher=false #Sample output",
            "switcher": "false",
            "#Sample": true,
            "output": true
          }}>{`=== Assistant Message ===
Tool Call: plaid_list_teams
Arguments:
{}
--------------------------------------------------------------------------------

Tool Result:
<team_id> (Example team)
--------------------------------------------------------------------------------

Tool Call: plaid_get_link_analytics
Arguments:
{
  "from_date": "2025-05-01",
  "team_id": "<team_id>",
  "to_date": "2025-05-08"
}
--------------------------------------------------------------------------------

Tool Result:
<Link analytics response>
--------------------------------------------------------------------------------

From 2025-05-01 through 2025-05-08:
- Link opens: 58
- Institution selections: 29
- Handoffs: 18
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h5 {...{
      "id": "expired-tokens"
    }}>{`Expired tokens`}</h5>
    <p>{`Access tokens for the MCP server expire after 15 minutes. If the access token expires, MCP requests fail with a `}<inlineCode parentName="p">{`401`}</inlineCode>{` status code or a provider-specific authentication error. Create a new token or call `}<inlineCode parentName="p">{`/oauth/token`}</inlineCode>{` with the `}<inlineCode parentName="p">{`refresh_token`}</inlineCode>{`.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Refreshing an access token",
        "switcher": "false",
        "#Refreshing": true,
        "an": true,
        "access": true,
        "token": true
      }}>{`curl -X POST https://production.plaid.com/oauth/token \\
  -H 'Content-Type: application/json' \\
  -d '{
    "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
    "client_secret": "YOUR_PRODUCTION_SECRET",
    "refresh_token": "YOUR_REFRESH_TOKEN",
    "grant_type": "refresh_token"
  }'
`}</code></pre>
    <h4 {...{
      "id": "dashboard-mcp-tools"
    }}>{`Dashboard MCP tools`}</h4>
    <p>{`The Dashboard MCP server currently supports the following tools:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Tool`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Purpose`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid_debug_item`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Diagnose a Plaid Item using related metadata.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid_get_link_analytics`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retrieve Link funnel, conversion, and error metrics.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid_get_tools_introduction`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Get usage guidance for Dashboard MCP tools.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid_get_usages`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Retrieve product usage metrics and API request volumes.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`plaid_list_teams`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`List teams available to the OAuth token.`}</td>
        </tr>
      </tbody>
    </table>
    <p>{`For more details about the tools provided by the Dashboard MCP server, connect to the server using a tool such as the `}<a parentName="p" {...{
        "href": "https://github.com/modelcontextprotocol/inspector"
      }}>{`MCP Inspector`}</a>{`.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "connection-options",
        level: 4,
        title: "Connection options",
      },
{
        id: "dashboard-mcp-tools",
        level: 4,
        title: "Dashboard MCP tools",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
